import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useCan } from '@/shared/session/profiles';
import { SUPPLIERS_PAGE } from '@/shared/tariffs/api';
import type { Supplier } from '@/shared/tariffs/types';
import { tariffDate } from '@/shared/tariffs/types';
import { useSuppliers } from '@/shared/tariffs/useTariffs';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { CardList, CardListItem } from '@/shared/ui/CardList';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { FolderIcon, PlusIcon } from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';

import { CatalogShelf } from './Catalogs';
import { SupplierDialog } from './SupplierDialog';
import { TariffSearch } from './TariffSearch';

const HELP = [
  <>
    La caja busca en las <strong>tarifas vigentes</strong> de todos los proveedores: por el EAN, por
    la referencia (escrita como quieras, con o sin puntos), por su principio o por palabras de la
    descripción.
  </>,
  <>
    Cada artículo sale con su <strong>PVP</strong>, el <strong>descuento habitual</strong> de su
    familia y el precio con ese descuento. Pulsa uno para ver todo lo que dice la tarifa.
  </>,
  <>
    Una tarifa nueva se sube desde la ficha del proveedor, o desde el correo que la trae con «Cargar
    como tarifa». No se busca en ella hasta que alguien la publica.
  </>,
  <>
    Los <strong>catálogos</strong> se abren aquí y en el móvil sin bajarlos enteros. Selecciona una
    referencia en el catálogo y sale al lado lo que dice la tarifa de ese proveedor.
  </>,
];

/** The tariffs of a supplier in one line: the one in force and its date. */
function tariffsLine(supplier: Supplier): string {
  const lists = supplier.price_lists ?? [];
  if (lists.length === 0) return 'Sin tarifas';
  return lists
    .map((list) =>
      list.current
        ? `${list.name} del ${tariffDate(list.current.tariff_date)}`
        : `${list.name}, sin publicar`,
    )
    .join(' · ');
}

/**
 * «Documentos» (contract v10.48): the search over the tariffs in force, and the suppliers.
 * Everybody with a profile sees it (`documents`); what each one sees of the prices is the
 * profile's (ADR 0042 of the back). Uploading and publishing is for whoever manages the
 * documents. Between them, the catalogs ready to open (contract v10.49).
 */
export function DocumentsHome() {
  const navigate = useNavigate();
  const manage = useCan('manage_documents');
  const [offset, setOffset] = useState(0);
  const suppliers = useSuppliers('', offset);
  const [adding, setAdding] = useState(false);
  const items = suppliers.data?.items ?? [];
  const total = suppliers.data?.total ?? 0;

  const newButton = manage ? (
    <Button icon={<PlusIcon size={18} />} onClick={() => setAdding(true)}>
      Nuevo proveedor
    </Button>
  ) : undefined;

  return (
    <>
      <PageHeader title="Documentos" help={HELP} actions={newButton} />

      {adding ? (
        <SupplierDialog
          onDone={(saved) => {
            setAdding(false);
            if (saved) void navigate(`/documentos/proveedores/${saved.id}`);
          }}
        />
      ) : null}

      <TariffSearch />

      <CatalogShelf />

      <h2 className="text-ink mt-4 text-[15px] font-semibold">Proveedores</h2>
      {suppliers.isPending ? <Spinner variant="block" label="Cargando proveedores" /> : null}
      {suppliers.isError ? (
        <ErrorMessage error={suppliers.error}>
          No se pudo cargar la lista de proveedores
        </ErrorMessage>
      ) : null}
      {suppliers.data && items.length === 0 ? (
        <EmptyState
          icon={<FolderIcon size={24} />}
          title="Todavía no hay proveedores"
          hint={
            manage
              ? 'Da de alta el primero y sube su tarifa, o cárgala desde el correo que la trae.'
              : 'Cuando quien gestiona los documentos suba las tarifas, saldrán aquí.'
          }
          action={newButton}
        />
      ) : null}
      {items.length > 0 ? (
        <CardList label="Proveedores" className="sm:grid sm:grid-cols-2 xl:grid-cols-3">
          {items.map((supplier) => {
            const pending = manage && (supplier.price_lists ?? []).some((list) => list.pending);
            return (
              <CardListItem
                key={supplier.id}
                to={`/documentos/proveedores/${supplier.id}`}
                title={supplier.name}
                subtitle={tariffsLine(supplier)}
                aside={pending ? <Badge tone="warn">Por publicar</Badge> : undefined}
              />
            );
          })}
        </CardList>
      ) : null}
      {total > SUPPLIERS_PAGE ? (
        <div className="text-ink-3 flex items-center justify-end gap-3 text-[13px]">
          <span>
            {offset + 1}–{offset + items.length} de {total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset((current) => Math.max(0, current - SUPPLIERS_PAGE))}
          >
            Anteriores
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset + SUPPLIERS_PAGE >= total}
            onClick={() => setOffset((current) => current + SUPPLIERS_PAGE)}
          >
            Siguientes
          </Button>
        </div>
      ) : null}
    </>
  );
}
